import React, { useCallback, useEffect, useState } from 'react';
import { useImmer } from 'use-immer';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router';
import { useTheme } from '@emotion/react';

import { SavingBar } from '@/components/atoms/SavingBar';
import Loader from '@/components/atoms/Loader';
import { stringifyError } from '@/utils/errors';
import { Location, MarketplaceChannelId } from '@/services/innovorder/marketplace/marketplace.type';
import { useGetLocationsQuery, usePutLocationsMutation } from '@/services/innovorder/marketplace/marketplace.endpoints';
import { getUserToken } from '@/redux/user';
import { ConfigContainer } from '../common/style/Common.style';
import ConfigurationPanel from '../common/ConfigurationPanel';
import UberEatsPanel from './UbereatsPanel/UbereatsPanel';
import { computeText } from '@/locales/utils';
import { useIntl } from 'react-intl';

const UberEatsConfig: React.FunctionComponent<React.PropsWithChildren> = () => {
    const token = useSelector(getUserToken);
    const { brandId, restaurantId } = useParams<{ brandId: string; restaurantId: string }>();
    const [currentLocations, setCurrentLocations] = useImmer<Location[]>([]);
    const [changes, setChanges] = useState(false);
    const intl = useIntl();
    const {
        data,
        isLoading,
        error: loadError,
    } = useGetLocationsQuery({ brandId, restaurantId, token, marketplace: 'ubereats' });
    const [putLocations, { isLoading: isSaving, error: saveError }] = usePutLocationsMutation();
    const theme = useTheme();

    const initLocations = useCallback(() => {
        if (!isLoading && data) {
            const formattedLocations = data
                .map((location) => ({
                    id: location.storeReference,
                    name: location.storeDisplayName || computeText(intl, 'configuration.error.unaccessible'),
                    marketplaces: location.channels.map((channel) => ({
                        id: channel.channelId,
                        active: channel.isEnabled,
                    })),
                    externalStoreBrandId: location.externalStoreBrandId,
                }))
                .sort((prev, next) => prev.name.localeCompare(next.name));
            setCurrentLocations(formattedLocations);
            setChanges(false);
        }
    }, [setCurrentLocations, data, isLoading]);

    const addLocation = useCallback(
        (storeReference: string, storeName: string) => {
            setCurrentLocations((draft) => {
                draft.push({
                    id: storeReference,
                    name: storeName,
                    marketplaces: [
                        {
                            id: MarketplaceChannelId.UberEats,
                            active: true,
                            enable: true,
                        },
                    ],
                    externalStoreBrandId: null,
                });
            });
            setChanges(true);
        },
        [setCurrentLocations],
    );

    const deleteLocation = useCallback(
        (locationId: string) => {
            setCurrentLocations((draft) => draft.filter(({ id }) => id !== locationId));
            setChanges(true);
        },
        [setCurrentLocations],
    );

    const toggleMarketplace = useCallback(
        (locationId: string, marketplaceId: MarketplaceChannelId) => {
            setCurrentLocations((draft) => {
                const location = draft.find(({ id }) => id === locationId);
                if (!location) return;

                const marketplace = location.marketplaces.find(({ id }) => id === marketplaceId);
                if (marketplace) {
                    marketplace.active = !marketplace.active;
                } else {
                    location.marketplaces.push({
                        id: marketplaceId,
                        active: true,
                    });
                }
            });
            setChanges(true);
        },
        [setCurrentLocations],
    );

    const handleSave = () => {
        if (isSaving) return;

        const formattedLocations = currentLocations.map((location) => ({
            storeReference: location.id,
            channels: location.marketplaces.map((channel) => ({
                channelId: channel.id,
                isEnabled: Boolean(channel.active),
            })),
        }));
        putLocations({ brandId, restaurantId, token, locations: formattedLocations, marketplace: 'ubereats' });
    };

    useEffect(() => {
        initLocations();
    }, [initLocations]);

    if (isLoading) {
        return <Loader color={theme.color.primary} size={40} withContainer />;
    }

    return (
        <>
            <ConfigContainer>
                <UberEatsPanel />
                <ConfigurationPanel
                    locations={currentLocations}
                    addLocation={addLocation}
                    deleteLocation={deleteLocation}
                    toggleMarketplace={toggleMarketplace}
                    marketplace="ubereats"
                />
            </ConfigContainer>
            <SavingBar
                onSave={handleSave}
                error={stringifyError(loadError) || stringifyError(saveError)}
                loading={isSaving}
                changesCount={changes ? 1 : 0}
                displayChangesCount={false}
                onCancel={initLocations}
            />
        </>
    );
};

export default UberEatsConfig;
